import { useCallback, useEffect, useMemo, useState } from 'react';
import { InstrumentStoreItem } from '../../types';
import { countDecimals, countIntegerPart, getFormatter, getLongestDecimal, getLongestIntegerPart } from '../../utils';

type OrderBookInstrumentCommon = {
  id: string;
  isMarketPrice: boolean;
};

export type OrderBookBuyInstrument = {
  direction: 'buy';
  buyQuantity: number;
  buyPrice: number;
} & OrderBookInstrumentCommon;

export type OrderBookSellInstrument = {
  direction: 'sell';
  sellQuantity: number;
  sellPrice: number;
} & OrderBookInstrumentCommon;

export type UseOrderBookCommonTranslation = {
  marketPrice: string;
};

export type UseOrderBookCommonParams = {
  translation: UseOrderBookCommonTranslation;
  instrumentStoreItem: InstrumentStoreItem | undefined;
};

export const useOrderBookCommon = ({ translation, instrumentStoreItem }: UseOrderBookCommonParams) => {
  const [buyRowData, setBuyRowData] = useState<OrderBookBuyInstrument[]>([]);
  const [sellRowData, setSellRowData] = useState<OrderBookSellInstrument[]>([]);
  const { formatNumber, getDecimalSeparator } = getFormatter();

  const longestBuyPriceDecimal = useMemo(() => {
    return getLongestDecimal(buyRowData.map(row => row.buyPrice));
  }, [buyRowData]);

  const longestBuyPriceIntegerPart = useMemo(() => {
    return getLongestIntegerPart(buyRowData.map(row => row.buyPrice));
  }, [buyRowData]);

  const longestSellPriceDecimal = useMemo(() => {
    return getLongestDecimal(sellRowData.map(row => row.sellPrice));
  }, [sellRowData]);

  const longestSellPriceIntegerPart = useMemo(() => {
    return getLongestIntegerPart(sellRowData.map(row => row.sellPrice));
  }, [sellRowData]);

  const formatSellPrice = useCallback(
    (price: number) => {
      const integerPart = countIntegerPart(price);
      const decimalsCount = countDecimals(price);
      const decimalDiff = longestSellPriceDecimal - decimalsCount;
      const integerPartdiff = longestSellPriceIntegerPart - integerPart;
      const decimalPointIfNeeded = decimalsCount === 0 && longestSellPriceDecimal > 0 ? getDecimalSeparator() : '';

      return '\u2007'.repeat(integerPartdiff) + formatNumber(price) + decimalPointIfNeeded + '0'.repeat(decimalDiff);
    },
    [longestSellPriceDecimal, formatNumber, longestSellPriceIntegerPart, getDecimalSeparator],
  );

  const formatBuyPrice = useCallback(
    (price: number) => {
      const integerPart = countIntegerPart(price);
      const decimalsCount = countDecimals(price);
      const decimalDiff = longestBuyPriceDecimal - decimalsCount;
      const integerPartDiff = longestBuyPriceIntegerPart - integerPart;
      const decimalPointIfNeeded = decimalsCount === 0 && longestBuyPriceDecimal > 0 ? getDecimalSeparator() : '';

      return '\u2007'.repeat(integerPartDiff) + formatNumber(price) + decimalPointIfNeeded + '0'.repeat(decimalDiff);
    },
    [longestBuyPriceDecimal, formatNumber, longestBuyPriceIntegerPart, getDecimalSeparator],
  );

  const formatPrice = useCallback(
    (row: OrderBookBuyInstrument | OrderBookSellInstrument) => {
      if (row.isMarketPrice) {
        return translation.marketPrice;
      } else if (row.direction === 'buy') {
        return formatBuyPrice(row.buyPrice);
      } else {
        return formatSellPrice(row.sellPrice);
      }
    },
    [formatBuyPrice, formatSellPrice, translation],
  );

  useEffect(() => {
    if (instrumentStoreItem) {
      setBuyRowData(
        (instrumentStoreItem.book?.bids || []).map((bid: Array<number>, ind: number) => ({
          id: `bid-${ind}`,
          buyPrice: bid[0],
          buyQuantity: bid[1],
          isMarketPrice: ind === 0 && !!instrumentStoreItem.book?.isBidMarketPrice,
          direction: 'buy',
        })),
      );

      setSellRowData(
        (instrumentStoreItem.book?.asks || []).map((ask: Array<number>, ind: number) => ({
          id: `ask-${ind}`,
          sellPrice: ask[0],
          sellQuantity: ask[1],
          isMarketPrice: ind === 0 && !!instrumentStoreItem.book?.isAskMarketPrice,
          direction: 'sell',
        })),
      );
    } else {
      setBuyRowData([]);
      setSellRowData([]);
    }
  }, [instrumentStoreItem]);

  return {
    buyRowData,
    sellRowData,
    formatPrice,
  };
};
